import { useEffect, useState } from "react";
import client, { apiError } from "@/api/client";
import { PageHeader, Section } from "@/components/PageChrome";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { useUserNames } from "@/hooks/useUserNames";
import { toast } from "sonner";
import { Barcode, ArrowRight } from "@phosphor-icons/react";

export default function Scanning() {
  const [pending, setPending] = useState([]);
  const [orderId, setOrderId] = useState("");
  const [pieces, setPieces] = useState("");
  const [boxes, setBoxes] = useState("");
  const [weight, setWeight] = useState("");
  const [billValue, setBillValue] = useState("");
  const [billedQty, setBilledQty] = useState("");
  const [wrongPickedQty, setWrongPickedQty] = useState("");
  const { nameFor } = useUserNames();

  const load = async () => {
    const { data } = await client.get("/orders/pending-scanning");
    setPending(data);
  };
  useEffect(() => { load(); }, []);

  const selectedOrder = pending.find((o) => o.id === orderId);

  const submit = async () => {
    if (!orderId || !pieces) return toast.error("Select order & enter pieces");
    try {
      await client.post("/orders/scan", {
        order_id: orderId,
        total_pieces: Number(pieces),
        total_boxes: Number(boxes || 0),
        weight: Number(weight || 0),
        bill_value: Number(billValue || 0),
        billed_quantity: Number(billedQty || 0),
        wrong_picked_quantity: Number(wrongPickedQty || 0),
      });
      toast.success("Scanned & counted");
      setOrderId(""); setPieces(""); setBoxes(""); setWeight("");
      setBillValue(""); setBilledQty(""); setWrongPickedQty(""); load();
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div>
      <PageHeader title="Scanning & Counting" subtitle="Stage 3 · Scanner Desk" />
      <Section>
        <div className="grid lg:grid-cols-3 gap-6">
          <div className="lg:col-span-1 bg-blue-50 border border-blue-200 border-l-4 border-l-blue-500 rounded-sm p-5">
            <div className="label-cap text-blue-800">New Scan Entry</div>
            <h3 className="font-display text-xl font-bold mt-1 text-blue-900 mb-4">Scan & count</h3>

            <label className="label-cap">Order No</label>
            <Select value={orderId} onValueChange={setOrderId}>
              <SelectTrigger data-testid="scan-order-select" className="rounded-sm mt-1 bg-white"><SelectValue placeholder="Select picked order"/></SelectTrigger>
              <SelectContent>
                {pending.map(o=><SelectItem key={o.id} value={o.id}>{o.order_no} — {o.retailer_name}</SelectItem>)}
              </SelectContent>
            </Select>

            {selectedOrder && (
              <div className="mt-3 bg-white border border-blue-200 rounded-sm px-3 py-2 text-xs">
                <div className="label-cap text-blue-700">Order Quantity</div>
                <div className="font-mono text-lg font-bold text-blue-900">{selectedOrder.order_quantity}</div>
              </div>
            )}

            <label className="label-cap block mt-4">Total Pieces in this Bill</label>
            <Input data-testid="scan-pieces-input" type="number" value={pieces} onChange={(e)=>setPieces(e.target.value)} className="mt-1 bg-white"/>

            <label className="label-cap block mt-4">Total Boxes</label>
            <Input data-testid="scan-boxes-input" type="number" value={boxes} onChange={(e)=>setBoxes(e.target.value)} className="mt-1 bg-white"/>

            <label className="label-cap block mt-4">Total Weight in this Bill</label>
            <Input data-testid="scan-weight-input" type="number" step="0.01" value={weight} onChange={(e)=>setWeight(e.target.value)} className="mt-1 bg-white"/>

            <label className="label-cap block mt-4">Billed Quantity</label>
            <Input data-testid="scan-billed-qty-input" type="number" value={billedQty} onChange={(e)=>setBilledQty(e.target.value)} className="mt-1 bg-white"/>

            <label className="label-cap block mt-4">Wrong Picked Quantity</label>
            <Input data-testid="scan-wrong-picked-qty-input" type="number" value={wrongPickedQty} onChange={(e)=>setWrongPickedQty(e.target.value)} className="mt-1 bg-white"/>

            <label className="label-cap block mt-4">Bill Value</label>
            <Input data-testid="scan-bill-value-input" type="number" step="0.01" value={billValue} onChange={(e)=>setBillValue(e.target.value)} className="mt-1 bg-white"/>

            <Button data-testid="scan-submit-btn" onClick={submit} className="w-full mt-4 bg-blue-600 hover:bg-blue-700 rounded-sm">
              <Barcode size={16} weight="bold" className="mr-1"/> Confirm Count <ArrowRight size={16} className="ml-1"/>
            </Button>
          </div>

          <div className="lg:col-span-2 bg-white border border-slate-200 rounded-sm">
            <div className="px-5 py-3 border-b border-slate-200"><div className="label-cap">Awaiting Scan · {pending.length}</div></div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead><tr className="text-left label-cap border-b border-slate-200 bg-slate-50">
                  <th className="px-4 py-2">Order No</th><th className="px-4 py-2">Retailer</th>
                  <th className="px-4 py-2">Order Qty</th><th className="px-4 py-2">Picked By</th><th className="px-4 py-2">Picked At</th>
                </tr></thead>
                <tbody>
                  {pending.map(o=>(
                    <tr key={o.id} className="border-b border-slate-100 hover:bg-blue-50">
                      <td className="px-4 py-2 font-mono">{o.order_no}</td>
                      <td className="px-4 py-2">{o.retailer_name}</td>
                      <td className="px-4 py-2 font-mono">{o.order_quantity}</td>
                      <td className="px-4 py-2 text-xs">{nameFor(o.picking?.mobile)}</td>
                      <td className="px-4 py-2 text-xs text-slate-600">{o.picking && new Date(o.picking.at).toLocaleString()}</td>
                    </tr>
                  ))}
                  {pending.length===0 && <tr><td colSpan={5} className="px-4 py-10 text-center text-slate-500">Nothing to scan</td></tr>}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </Section>
    </div>
  );
}
